import { COLLATION_LOCALE, type BoardCard } from "@salesboard/contracts";

/**
 * Within-cell order. **Derived, never manual** — which is the decision that makes a capped
 * cell a legitimate drop target: a drop never has to choose a position, so dropping on a
 * summarised cell means exactly what dropping on an expanded one means.
 *
 * Stalled first, because a meeting must not miss stagnation behind seven healthier cards;
 * then value descending; then name, so the order is total and a re-render cannot reshuffle
 * equal cards under someone's cursor.
 */
export function orderWithinCell(cards: BoardCard[]): BoardCard[] {
  return [...cards].sort(
    (a, b) =>
      Number(b.stalled) - Number(a.stalled) ||
      Number(b.nip) - Number(a.nip) ||
      // The application's fixed collation locale — **not** the reader's, and not the
      // browser's. The tie-break decides what two viewers in a meeting see, so it must not
      // depend on whose laptop is showing the board *or* on which language either of them
      // chose in their profile menu. Display formatting is personal; card order is shared.
      a.name.localeCompare(b.name, COLLATION_LOCALE),
  );
}

/**
 * Cards keyed by `stageId:quarter`, each cell ordered.
 *
 * `pending` moves exactly one card to the cell it is going to. This is the commit model's
 * visible half: the presentation responds immediately while the cache still holds only
 * committed server truth, so the card is where the user put it and every count, total and
 * other card remains what the server last reported. Removing this would make a drop appear
 * to snap back to its old cell until the refetch lands — the behaviour the book forbids.
 */
export function groupIntoCells(
  cards: BoardCard[],
  pending?: { id: string; stageId: string; quarter: number },
): Map<string, BoardCard[]> {
  const byCell = new Map<string, BoardCard[]>();
  for (const card of cards) {
    const moved = pending && pending.id === card.id;
    const key = moved ? `${pending.stageId}:${pending.quarter}` : `${card.stageId}:${card.quarter}`;
    const list = byCell.get(key) ?? [];
    list.push(card);
    byCell.set(key, list);
  }
  for (const [key, list] of byCell) byCell.set(key, orderWithinCell(list));
  return byCell;
}
